// Timeline-driven SVG player.
//
// Strategy
// --------
// We rely on the shared `applyAt(timeline, ms)` runtime: it returns a flat
// list of selector → { transform, opacity, attrs, styles } resolutions for a
// given time. On each rAF we apply those to live SVG nodes.
//
// UX rules baked in
// -----------------
// 1. Cards mounted at the same time would otherwise loop in lock-step, so
//    every card on a page caught at t≈0 looks "blank". A `delayMs` prop
//    staggers each card's clock so the page always has visible logos.
// 2. The "loop pause" actually holds the END state (logo visible) for a
//    generous 2.4s — most of the time, viewers see the finished logo, not
//    the brief moment it's invisible at the start of a fade.
// 3. When a parent swaps the `timeline` (the user picks a new template in
//    the editor), we pre-roll showing the NEW end state for 250ms before
//    starting the animation — eliminates the "logo disappears for a beat"
//    artifact when changing templates.

import { useEffect, useRef } from "react";
import { applyAt } from "@app/shared/runtime";
import type { Timeline } from "@app/shared/ir";
import { measurePathLengths, applyResolution } from "./apply";

interface Props {
  svg: string;
  timeline: Timeline;
  loop?: boolean;
  autoplay?: boolean;
  width?: number;
  height?: number;
  paused?: boolean;
  /** Stagger this Player's clock by N ms — used for grids of cards. */
  delayMs?: number;
  /** When true (default), changing timeline holds the new end-state for
   *  250ms before starting the animation. Pass false to start immediately. */
  smoothSwap?: boolean;
  /** Drives the playhead from a parent (the editor scrubber). When set, the
   *  player ignores its internal clock. */
  controlledMs?: number;
}

const LOOP_END_HOLD_MS = 2400;
const SWAP_PRE_ROLL_MS = 250;

export function Player({ svg, timeline, loop, paused, controlledMs, width, height, delayMs = 0, smoothSwap = true }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const startedAt = useRef<number>(performance.now());
  const isFirstTimeline = useRef(true);

  // Inject SVG. Re-run when the SVG text changes (logo edit / template swap).
  useEffect(() => {
    if (!hostRef.current) return;
    hostRef.current.innerHTML = svg;
    const svgEl = hostRef.current.querySelector("svg");
    if (svgEl) {
      svgEl.removeAttribute("width");
      svgEl.removeAttribute("height");
      svgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
      svgEl.setAttribute("overflow", "visible");
      // If the source SVG is missing viewBox, derive one from the bounding
      // box so the mark centers properly. getBBox can throw on detached SVGs
      // — wrap it.
      if (!svgEl.getAttribute("viewBox")) {
        try {
          const bb = (svgEl as unknown as SVGGraphicsElement).getBBox();
          if (bb.width > 0 && bb.height > 0) {
            svgEl.setAttribute("viewBox", `${bb.x} ${bb.y} ${bb.width} ${bb.height}`);
          }
        } catch { /* ignore */ }
      }
      // Wrap children so [data-logo-root] is the single transform anchor.
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.setAttribute("data-logo-root", "");
      while (svgEl.firstChild && svgEl.firstChild !== g) g.appendChild(svgEl.firstChild);
      svgEl.appendChild(g);
    }
    measurePathLengths(hostRef.current);
    // SVG was freshly mounted — paint the end state immediately so the
    // first visible frame is a complete logo, not opacity 0.
    if (hostRef.current && timeline) {
      const finalState = applyAt(timeline, timeline.durationMs);
      for (const a of finalState) applyResolution(hostRef.current, a);
    }
    isFirstTimeline.current = true;
  }, [svg]);

  useEffect(() => {
    if (paused) return;
    let stopped = false;

    // First timeline after a fresh SVG mount → start immediately, with the
    // caller's stagger offset. Subsequent timeline changes → pre-roll on
    // the new end-state for a beat so the swap feels intentional.
    const preRoll = isFirstTimeline.current ? 0 : (smoothSwap ? SWAP_PRE_ROLL_MS : 0);
    isFirstTimeline.current = false;
    startedAt.current = performance.now() + delayMs + preRoll;

    const tick = () => {
      if (stopped || !hostRef.current) return;
      const raw = controlledMs != null ? controlledMs : performance.now() - startedAt.current;
      let ms: number;
      if (raw < 0) {
        // Pre-roll / pre-stagger window: hold at the end state so the logo
        // is fully visible while we wait to start.
        ms = timeline.durationMs;
      } else if (loop) {
        const cycle = timeline.durationMs + LOOP_END_HOLD_MS;
        const r = raw % cycle;
        ms = r > timeline.durationMs ? timeline.durationMs : r;
      } else {
        ms = Math.min(raw, timeline.durationMs);
      }
      const applied = applyAt(timeline, ms);
      for (const a of applied) applyResolution(hostRef.current, a);
      if (controlledMs == null) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [timeline, loop, paused, controlledMs, delayMs, smoothSwap]);

  return (
    <div
      ref={hostRef}
      className="player-host relative"
      style={{
        aspectRatio: width && height ? `${width}/${height}` : "1/1",
      }}
    />
  );
}
